import React, { useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  Card,
  Box,
  Stack,
  CircularProgress,
  TextField as MuiTextField,
  FormControl,
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  Chip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from '../../../components/Page';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { behaviourService, BehaviourTypeCategory, CreateBehaviourTypeDTO } from 'src/services/behaviourService';
import { useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';

interface FormValues {
  title: string;
  description: string;
  type: 'highlight' | 'challenge' | '';
  points: number;
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must not exceed 100 characters'),
  description: Yup.string()
    .required('Description is required')
    .min(5, 'Description must be at least 5 characters')
    .max(500, 'Description must not exceed 500 characters'),
  type: Yup.string().oneOf(['highlight', 'challenge'], 'Invalid category selection').required('Category is required'),
  points: Yup.number().required('Points value is required').min(1, 'Points must be at least 1').max(500, 'Points cannot exceed 500'),
});

export default function AddBehaviorType() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getBaseRoute } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'S.T.R.E.E.T Cred', path: getBaseRoute('/street-cred') },
      { label: 'New Behavior Type' },
    ],
  });

  const handleCancel = () => {
    navigate(getBaseRoute('/street-cred'), { state: { tab: 1 } });
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      title: '',
      description: '',
      type: 'highlight',
      points: 10,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const dto: CreateBehaviourTypeDTO = {
          title: values.title.trim(),
          description: values.description.trim(),
          type: values.type as BehaviourTypeCategory,
          points: Number(values.points),
        };

        await behaviourService.createBehaviourType(dto);
        toast.success('Behavior type created successfully');
        queryClient.invalidateQueries({ queryKey: ['behaviourTypes'] });
        queryClient.invalidateQueries({ queryKey: ['streetCredMetrics'] });
        navigate(getBaseRoute('/street-cred'), { state: { tab: 1 } });
      } catch (error: any) {
        toast.error(error?.message || 'Failed to create behavior type');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const isHighlight = formik.values.type === 'highlight';

  return (
    <Page title="Add Behavior Type | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5 }}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<Icon icon="solar:arrow-left-linear" width={16} />}
            onClick={handleCancel}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.75rem',
              mb: 0.5,
              textTransform: 'none',
              px: 0.5,
              '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
            }}
          >
            Back to Catalog
          </Button>

          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.25rem', lineHeight: 1.2 }}>
            Add New Behavior Type
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Define school character milestones, awards, or disciplinary challenge types.
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {/* Form Fields Card */}
            <Grid item xs={12} md={7.5}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Stack spacing={2}>
                  {/* Title */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                      Behavior Title *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      size="small"
                      placeholder="e.g. Peer Leadership, Homework Completion, Disruptive Conduct"
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      error={Boolean(formik.touched.title && formik.errors.title)}
                      helperText={formik.touched.title && formik.errors.title}
                      InputProps={{
                        sx: { borderRadius: 1.5, fontSize: '0.8125rem' },
                      }}
                    />
                  </Box>

                  {/* Category and Points */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                        Category *
                      </Typography>
                      <FormControl fullWidth size="small" error={Boolean(formik.touched.type && formik.errors.type)}>
                        <MuiSelect
                          name="type"
                          value={formik.values.type}
                          onChange={formik.handleChange}
                          sx={{ borderRadius: 1.5, fontSize: '0.8125rem' }}
                        >
                          <MenuItem value="highlight">Positive Highlight (+)</MenuItem>
                          <MenuItem value="challenge">Demerit Challenge (-)</MenuItem>
                        </MuiSelect>
                        {formik.touched.type && formik.errors.type && (
                          <FormHelperText error>{formik.errors.type}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                        Default Points Value *
                      </Typography>
                      <MuiTextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="10"
                        name="points"
                        value={formik.values.points}
                        onChange={formik.handleChange}
                        error={Boolean(formik.touched.points && formik.errors.points)}
                        helperText={formik.touched.points && formik.errors.points}
                        InputProps={{
                          sx: { borderRadius: 1.5, fontSize: '0.8125rem' },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Description */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                      Description & Criteria *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      placeholder="Explain when teachers or staff should log this behavior..."
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      error={Boolean(formik.touched.description && formik.errors.description)}
                      helperText={formik.touched.description && formik.errors.description}
                      InputProps={{
                        sx: { borderRadius: 1.5, fontSize: '0.8125rem' },
                      }}
                    />
                  </Box>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        height: 38,
                        borderColor: 'divider',
                        color: 'text.secondary',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Icon icon="solar:check-read-bold" width={16} />}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 3,
                        height: 38,
                        boxShadow: 'none',
                      }}
                    >
                      {isSubmitting ? 'Creating...' : 'Save Behavior Type'}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Grid>

            {/* Live Preview Card */}
            <Grid item xs={12} md={4.5}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  position: 'sticky',
                  top: 20,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, display: 'block', fontSize: '0.6875rem' }}>
                  Catalog Card Preview
                </Typography>

                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isHighlight ? '#A7F3D0' : '#FECACA',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Chip
                      label={isHighlight ? 'Positive Highlight' : 'Demerit Challenge'}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: isHighlight ? '#059669' : '#DC2626',
                      }}
                    />
                    <Chip
                      label={`${isHighlight ? '+' : '-'}${formik.values.points || 0} pts`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        bgcolor: isHighlight ? '#059669' : '#DC2626',
                        color: '#FFFFFF',
                      }}
                    />
                  </Stack>

                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.95rem', mb: 0.5 }}>
                    {formik.values.title || 'Behavior Title Preview'}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 1.5 }}>
                    {formik.values.description || 'Description criteria will appear here...'}
                  </Typography>

                  <Box sx={{ pt: 1.5, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6875rem' }}>
                      Status: Active in library
                    </Typography>
                    <Icon icon={isHighlight ? 'solar:star-bold' : 'solar:shield-warning-bold'} width={16} color={isHighlight ? '#059669' : '#DC2626'} />
                  </Box>
                </Card>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Page>
  );
}
